import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { Star, Plus, Heart } from "lucide-react";

export const ProductCard = ({ product, brandName, index = 0 }) => {
  const navigate = useNavigate();
  const { add } = useCart();
  const { toggle, has } = useWishlist();
  const saved = has(product.id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08 }}
      className="group text-left w-full cursor-pointer"
      onClick={() => navigate(`/product/${product.id}`)}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative overflow-hidden bg-[#faf7f7] border border-zinc-100">
        {product.badge && (
          <span className="absolute top-4 left-4 z-10 text-[9px] uppercase tracking-[0.25em] bg-gold text-white px-3 py-1.5">
            {product.badge}
          </span>
        )}
        {product.on_sale && (
          <span className={`absolute ${product.badge ? "top-12" : "top-4"} left-4 z-10 text-[9px] uppercase tracking-[0.25em] bg-red-500 text-white px-3 py-1.5`} data-testid={`sale-ribbon-${product.id}`}>
            Sale
          </span>
        )}
        {!product.in_stock && (
          <span className="absolute top-4 right-4 z-10 text-[9px] uppercase tracking-[0.25em] bg-zinc-100 text-zinc-500 px-3 py-1.5">
            Sold Out
          </span>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggle(product.id);
            toast.success(saved ? "Removed from wishlist" : "Saved to wishlist");
          }}
          data-testid={`wishlist-toggle-${product.id}`}
          className={`absolute bottom-4 right-4 z-10 p-2.5 border transition-colors duration-300 ${saved ? "bg-gold border-gold text-white" : "bg-white/80 border-zinc-200 text-zinc-500 hover:text-gold hover:border-gold"}`}
          aria-label="Toggle wishlist"
        >
          <Heart size={14} className={saved ? "fill-white" : ""} />
        </button>
        {product.image && (
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="w-full aspect-[3/4] object-contain p-6 group-hover:scale-105 transition-transform duration-700 ease-out"
          />
        )}
      </div>
      <div className="pt-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{brandName}</p>
          <h3 className="font-serif text-xl text-zinc-900 mt-1.5 leading-snug group-hover:text-champagne transition-colors duration-300 line-clamp-2">
            {product.name}
          </h3>
          <div className="flex items-center gap-3 mt-2.5">
            <span className="text-gold font-medium tracking-wide">{inr(product.price)}</span>
            {product.compare_at && product.compare_at > product.price && (
              <span className="text-zinc-400 text-sm line-through">{inr(product.compare_at)}</span>
            )}
            {product.rating && (
              <span className="flex items-center gap-1 text-xs text-zinc-400">
                <Star size={11} className="fill-gold text-gold" />
                {product.rating}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            add(product);
            toast.success("Added to your bag");
          }}
          data-testid={`add-to-bag-${product.id}`}
          className="shrink-0 mt-1 p-2.5 border border-gold/40 text-gold hover:bg-gold hover:text-white transition-colors duration-300"
          aria-label="Add to bag"
        >
          <Plus size={14} />
        </button>
      </div>
    </motion.div>
  );
};
